import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useCountryProviders from './useCountryProviders';
import Loading from 'Components/Atoms/Loading/Loading';
import useCountries from 'Hooks/useCountries';
import { useTranslation } from 'react-i18next';
import useWorkspaceSelected from 'Hooks/useWorkspaceSelected';
import useSettingsStorage from 'Hooks/useSettingsStorage';
import Select from 'Components/Atoms/Select/Select';
import Search from 'Components/Atoms/Search/Search';
import ItemSelector from 'Components/Molecules/ItemSelector/ItemSelector';

const LIMIT_PROVIDERS = 100;

export default function ProviderSelector(props) {
    const { onSelect, providerSelectedId } = props;

    const [searchBox, setSearchBox] = useState('');
    const { settings, setSetting } = useSettingsStorage();

    const resultsContainer = useRef(null);

    const { workspace } = useWorkspaceSelected();
    const [country, setCountry] = useState(workspace.country);

    const { t } = useTranslation('providers');

    useEffect(() => {
        if (resultsContainer.current) {
            resultsContainer.current.scrollTop = 0;
        }
    }, [searchBox, country]);

    useEffect(() => {
        setSearchBox('');
    }, []);

    const { isLoading: isCountriesLoading, countries } = useCountries();

    useEffect(() => {
        const storageCountry = settings.connection_country?.value;
        if (storageCountry && countries.some((c) => c.code === storageCountry)) {
            setCountry(storageCountry);
        } else {
            setCountry(workspace.country);
        }
    }, [settings.connection_country, workspace.country, countries]);

    const { availableProviders, isLoading: isCountryProviderLoading } = useCountryProviders(country);

    const renderProviders = availableProviders.filter(
        (provider) => !['banco manual', 'manual bank'].includes(provider.name.toLowerCase()),
    );

    const providersFiltered = renderProviders
        .filter((provider) => provider.service_root_provider_id === provider.service_provider_id)
        .filter((provider) => provider.name.toLowerCase().includes(searchBox.toLowerCase().trim()));

    const filteredItemsLength = searchBox !== '' ? providersFiltered.length : renderProviders.length;
    const hasMoreProviders = filteredItemsLength > LIMIT_PROVIDERS;
    const providersToShow =
        searchBox !== '' ? providersFiltered.slice(0, LIMIT_PROVIDERS) : renderProviders.slice(0, LIMIT_PROVIDERS);

    const handleChangedCountry = (country) => {
        setCountry(country);
        setSetting({
            field: 'connection_country',
            value: country,
            is_global: false,
        });
    };

    if (isCountriesLoading || isCountryProviderLoading) {
        return (
            <div className="h-[calc(100dvh-44px)] flex items-center justify-center">
                <Loading />
            </div>
        );
    }

    const countryOptions = countries.map((c) => ({
        value: c.code,
        label: c.name,
    }));

    return (
        <>
            <div className="text-center flex flex-col md:pt-8 lg:pt-20 items-center gap-2 pb-5 lg:pb-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="21" fill="#6050C6"></circle>
                    <path
                        fill="#fff"
                        d="M32.571 22.57A1.43 1.43 0 0 0 34 21.143V20.14c0-.526-.288-1.008-.752-1.258l-8.91-4.797a.71.71 0 0 0-.676 0l-8.91 4.797c-.463.25-.752.732-.752 1.258v1.002c0 .788.64 1.429 1.429 1.429h.714v7.143h-.714A1.43 1.43 0 0 0 14 31.142v1.429c0 .788.64 1.428 1.429 1.428H32.57A1.43 1.43 0 0 0 34 32.572v-1.429a1.43 1.43 0 0 0-1.429-1.428h-.714V22.57zM15.43 20.14 24 15.525l8.571 4.615v1.002H15.43zm9.285 9.574V22.57h2.143v7.143zm-3.571 0V22.57h2.143v7.143zM17.57 22.57h2.143v7.143h-2.143zm15.001 10H15.43v-1.429H32.57zm-2.143-2.857h-2.143V22.57h2.143z"
                    ></path>
                </svg>
                <h2 className="m-0 text-2xl md:text-3xl text-pretty font-semibold">
                    {t('select-the-bank-you-want-to-connect')}
                </h2>
            </div>

            <div className="sticky top-0 bg-white z-10 md:pt-2 -mx-1 px-1">
                <div className="flex items-center justify-between gap-2 md:gap-3">
                    <Select options={countryOptions} value={country} onChange={handleChangedCountry} enableFilter />
                    <Search
                        className="w-full"
                        placeholder={t('search-bank-placeholder')}
                        autoFocus
                        value={searchBox}
                        onChange={setSearchBox}
                    />
                </div>
            </div>

            <div ref={resultsContainer} className="pb-16">
                <div className="py-4 grid gap-3">
                    {providersFiltered.length === 0 && (
                        <div className="px-2 text-center text-gray-500">{t('no-results-found')}</div>
                    )}
                    {providersToShow.map((provider) => {
                        return (
                            <ItemSelector
                                key={provider.id}
                                isBusy={providerSelectedId === provider.id}
                                image={provider.logo_url}
                                title={provider.name}
                                onClick={() => {
                                    if (!providerSelectedId) {
                                        onSelect(provider);
                                    }
                                }}
                            />
                        );
                    })}

                    {hasMoreProviders && (
                        <div className="text-center text-gray-500 py-3 text-xs">
                            {t('find-your-bank-using-the-searcher')}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

ProviderSelector.propTypes = {
    onSelect: PropTypes.func.isRequired,
    providerSelectedId: PropTypes.string,
};
